import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  created_at: string;
  is_read?: boolean;
  metadata?: {
    caso_id?: string;
    conversation_id?: string;
    expires_at?: string;
  };
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [popupNotification, setPopupNotification] = useState<Notification | null>(null);
  const { user } = useAuth();

  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      // Buscar notificações do usuário
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .or(`is_global.eq.true,target_user_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (notificationsError) throw notificationsError;

      // Buscar quais notificações foram lidas
      const { data: readsData, error: readsError } = await supabase
        .from('notification_reads')
        .select('notification_id')
        .eq('user_id', user.id);

      if (readsError) throw readsError;

      const readNotificationIds = new Set(readsData?.map(r => r.notification_id) || []);
      
      const notificationsWithReadStatus = (notificationsData || []).map(notification => ({
        ...notification,
        metadata: notification.metadata as Notification['metadata'],
        is_read: readNotificationIds.has(notification.id)
      }));

      setNotifications(notificationsWithReadStatus);
      setUnreadCount(notificationsWithReadStatus.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notification_reads')
        .insert({
          notification_id: notificationId,
          user_id: user.id
        });

      if (error) throw error;

      // Atualizar estado local
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Clear popup if it's the same notification
      if (popupNotification?.id === notificationId) {
        setPopupNotification(null);
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const unreadNotifications = notifications.filter(n => !n.is_read);
    
    try {
      const inserts = unreadNotifications.map(n => ({
        notification_id: n.id,
        user_id: user.id
      }));

      if (inserts.length === 0) return;

      const { error } = await supabase
        .from('notification_reads')
        .insert(inserts);

      if (error) throw error;

      // Atualizar estado local
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      // Deletar da tabela notifications
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      // Remover do estado local
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Atualizar contador se era não lida
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Subscription para novas notificações
    if (user) {
      const channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications'
          },
          (payload) => {
            console.log('New notification received:', payload);
            const newNotification = payload.new as Notification;
            
            // Verificar se a notificação é para este usuário
            if ((newNotification as any).is_global || (newNotification as any).target_user_id === user.id) {
              setNotifications(prev => [{ ...newNotification, is_read: false }, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Mostrar pop-up para nova notificação
              setPopupNotification({ ...newNotification, is_read: false });
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'notifications'
          },
          (payload) => {
            console.log('Notification deleted:', payload);
            const deletedId = payload.old.id;
            
            setNotifications(prev => {
              const filtered = prev.filter(n => n.id !== deletedId);
              // Recalcular unread count
              const newUnreadCount = filtered.filter(n => !n.is_read).length;
              setUnreadCount(newUnreadCount);
              return filtered;
            });
            
            if (popupNotification?.id === deletedId) {
              setPopupNotification(null);
            }
          }
        )
        .subscribe();

      // Fallback: Check for new notifications every 2 minutes
      const pollInterval = setInterval(() => {
        fetchNotifications();
      }, 120000);

      return () => {
        // 🔧 CORREÇÃO DEFINITIVA: Cleanup resiliente com verificações múltiplas
        try {
          if (channel && 
              supabase?.realtime && 
              typeof supabase.removeChannel === 'function') {
            
            // Verificar estado da conexão antes de remover
            const isConnected = supabase.realtime.isConnected?.() ?? true;
            if (isConnected !== false) {
              supabase.removeChannel(channel);
            }
          }
        } catch (err) {
          // Log silencioso apenas em desenvolvimento
          if (process.env.NODE_ENV === 'development') {
            console.debug('[Realtime] Cleanup silencioso:', err);
          }
        }
        
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      };
    }
  }, [user]);

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
    popupNotification,
    clearPopup: () => setPopupNotification(null)
  };
}